import React, { useMemo } from 'react';
import { isNil } from 'lodash';
import { ScatterPlot } from '@nivo/scatterplot';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import {
  generateDefaultConfiguration,
  getColor,
  onIdeaPortalSettingsPage,
  openDrawerForDrillIn,
} from '../../helpers';
import AxesReset from '../../custom_layers/AxesReset';
import ScatterNode from './ScatterNode';
import { useChartScales } from '../ChartScales';
import d3 from 'd3';
import styles from '../Chart.module.css';
import classNames from 'classnames';

const DEFAULT_LAYERS = ['grid', 'nodes', AxesReset, 'markers'];

const POINT_SIZES = {
  small: 12,
  medium: 24,
  large: 34,
};

const POINT_RANGES = {
  small: [12, 30],
  medium: [18, 60],
  large: [24, 90],
};

function scaleValue(configValue) {
  if (isNil(configValue) || configValue === '') {
    return 'auto';
  } else {
    return configValue;
  }
}

function CommonScatterBubblePlot({
  data,
  meta,
  width,
  height,
  publishing,
  layers = null,
}) {
  const { get, set } = useReportConfiguration();
  const { margins } = useChartScales();

  const hasSize = Boolean(meta.reportColumnNames.size);
  const { minSize, maxSize } = useMemo(() => {
    let minSize;
    let maxSize;

    if (hasSize) {
      data.forEach(seriesData => {
        seriesData.data.forEach(({ size }) => {
          if (isNil(size)) return;
          if (typeof size === 'string') size = parseFloat(size);
          if (size < minSize || isNil(minSize)) minSize = size;
          if (size > maxSize || isNil(maxSize)) maxSize = size;
        });
      });
    }

    return {
      minSize,
      maxSize,
    };
  }, [data, hasSize]);

  let nodeSize;
  const pointSize = get('pointSize') || 'small';
  if (hasSize) {
    nodeSize = {
      key: 'size',
      values: [minSize, maxSize],
      sizes: POINT_RANGES[pointSize],
    };
    set(
      'percentileNodeSize',
      d3.quantile(POINT_RANGES[pointSize], get('percentile') / 100)
    );
  } else {
    nodeSize = POINT_SIZES[pointSize];
  }

  const { xAxisNumeric, yAxisNumeric } = useMemo(() => {
    const flatData = data.flatMap(seriesData => seriesData.data);

    return {
      xAxisNumeric: flatData.every(data => typeof data.x === 'number'),
      yAxisNumeric: flatData.every(data => typeof data.y === 'number'),
    };
  }, [data]);

  const drillInEnabled = meta.drill_in?.enabled && !onIdeaPortalSettingsPage();
  function handleClick(node) {
    // When publishing in a dashboard, we need the event target to get the dashboard panel id
    // from the DOM. However, the handleClick on ScatterPlot is wrong and passes in the `node` arg
    // twice to `handleClick` instead of the `node` and `event`. Seems like a bug in Nivo but can't update
    // due to react 17+ requirements.
    // So instead we pass `openDrawerForDrillInFromNode` to `ScatterNode` since that `handleClick` gets
    // the correct arguments
    if (drillInEnabled && !meta.drill_in.publishing) {
      openDrawerForDrillIn(node.data.column_index, null, meta, null);
    }
  }

  function openDrawerForDrillInFromNode(node, event) {
    if (meta.drill_in.publishing && drillInEnabled) {
      openDrawerForDrillIn(node.data.column_index, null, meta, event.target);
    }
  }

  const classes = classNames(styles.ScatterChartContainer, {
    [styles.clickable]: drillInEnabled,
  });

  return (
    <div className={classes}>
      <ScatterPlot
        {...generateDefaultConfiguration({
          configuration: get(),
          meta,
          width: width,
        })}
        margin={margins}
        width={width}
        height={height}
        data={data}
        animate={false}
        useMesh={false}
        enableGridX
        enableGridY
        xScale={
          xAxisNumeric
            ? {
                type: 'linear',
                min: scaleValue(get('horizontalMinValue')),
                max: scaleValue(get('horizontalMaxValue')),
              }
            : { type: 'point' }
        }
        yScale={
          yAxisNumeric
            ? {
                type: 'linear',
                min: scaleValue(get('minValue')),
                max: scaleValue(get('maxValue')),
              }
            : { type: 'point' }
        }
        colors={point => getColor(get(), meta, point.series)}
        nodeSize={nodeSize}
        layers={layers || DEFAULT_LAYERS}
        renderNode={nodeProps => (
          <ScatterNode
            {...nodeProps}
            openDrawerForDrillIn={openDrawerForDrillInFromNode}
          />
        )}
        onClick={handleClick}
      />
    </div>
  );
}

export default CommonScatterBubblePlot;
